import React, { useEffect } from 'react';
import SvgImage from '@/components/Module/SvgImage';
import {
  Container,
  PageLayout,
  StyledArrowButton,
  StyledNumberButton,
} from './styles';
import { useForm } from 'react-hook-form';
import { useDebounce } from 'usehooks-ts';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import classNames from 'classnames';

type PaginationBarProps = {
  isFixed?: boolean;
  isSticky?: boolean;
  currentPage: number;
  totalPage: number;
  handleClick(num: number): void;
};

const PaginationBar = (props: PaginationBarProps) => {
  const {
    isFixed = true,
    isSticky = false,
    currentPage,
    totalPage,
    handleClick,
  } = props;
  const renderPagination = () => {
    // 기존 로직
    if (totalPage < 10) {
      return Array.from({ length: totalPage }, (_, idx) => idx + 1);
    } else if (currentPage < 6) {
      return Array.from({ length: 9 }, (_, idx) => idx + 1);
    } else if (currentPage > totalPage - 4) {
      return [
        1,
        '•••',
        totalPage - 6,
        totalPage - 5,
        totalPage - 4,
        totalPage - 3,
        totalPage - 2,
        totalPage - 1,
        totalPage,
      ];
    } else {
      return [
        1,
        '•••',
        currentPage - 2,
        currentPage - 1,
        currentPage,
        currentPage + 1,
        currentPage + 2,
        currentPage + 3,
        currentPage + 4,
      ];
    }

    // else if (currentPage < 6) {
    //   return [1, 2, 3, 4, 5, 6, 7, '•••', totalPage];
    // } else if (currentPage > totalPage - 5) {
    //   return [
    //     1,
    //     '•••',
    //     totalPage - 6,
    //     totalPage - 5,
    //     totalPage - 4,
    //     totalPage - 3,
    //     totalPage - 2,
    //     totalPage - 1,
    //     totalPage,
    //   ];
    // } else {
    //   return [
    //     1,
    //     '•••',
    //     currentPage - 2,
    //     currentPage - 1,
    //     currentPage,
    //     currentPage + 1,
    //     currentPage + 2,
    //     '•••',
    //     totalPage,
    //   ];
    // }
  };

  const onClick = (num: number) => {
    handleClick(num);
  };

  const prevActive = currentPage !== 1;
  const nextActive = currentPage !== totalPage;

  const {
    register,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<{ numberInput: string }>({ defaultValues: { numberInput: '' } });
  const searchString = watch('numberInput');
  const debouncedValue = useDebounce(searchString, 500);
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';

  useEffect(() => {
    const sanitizedValue = debouncedValue.replace(/[^0-9]/g, ''); // 숫자가 아닌 문자를 제거
    if (sanitizedValue !== debouncedValue) {
      setValue('numberInput', sanitizedValue, { shouldValidate: true });
    }

    if (Number(sanitizedValue) > totalPage) {
      setError('numberInput', {
        type: 'max',
        message: isKo
          ? `최대 ${totalPage} 페이지까지 조회할 수 있습니다. 다시 입력해 주세요.`
          : `You can view up to ${totalPage} pages. Please try again.`,
      });
    } else {
      clearErrors('numberInput');
      parseInt(debouncedValue) > 0 && handleClick(parseInt(debouncedValue));
    }
  }, [debouncedValue]);

  return (
    <motion.div
      initial={{
        opacity: 0,
      }} // 초기 상태: 높이 0, 투명
      animate={{ opacity: 1 }} // 열릴 때: 높이 자동, 투명도 1
      transition={{ duration: 0.5, ease: 'easeInOut' }} // 전환 시간 및 속도
      style={
        isSticky
          ? {
              position: 'sticky',
              bottom: '24px',
              marginTop: 'auto',
              paddingTop: '20px',
              width: 'fit-content',
              marginLeft: 'auto',
              marginRight: 'auto',
            }
          : {}
      }
    >
      <Stack direction={'row'} justifyContent={'center'}>
        <Container className={classNames({ isFixed })}>
          <StyledArrowButton
            active={prevActive}
            style={{ transform: 'rotate(180deg)' }}
            disabled={!prevActive}
            onClick={() => prevActive && onClick(currentPage - 1)}
          >
            <SvgImage name={'page_nation_arrow'} />
          </StyledArrowButton>
          <PageLayout>
            {renderPagination().map((page, index) => (
              <StyledNumberButton
                active={page === currentPage}
                key={index}
                onClick={() => typeof page === 'number' && onClick(page)}
                disabled={page === '•••'}
              >
                {page}
              </StyledNumberButton>
            ))}
          </PageLayout>
          <StyledArrowButton
            active={nextActive}
            onClick={() => nextActive && onClick(currentPage + 1)}
          >
            <SvgImage name={'page_nation_arrow'} />
          </StyledArrowButton>
        </Container>
      </Stack>
    </motion.div>
  );
};

export default PaginationBar;
